/* eslint-disable camelcase */
import { createRestClient } from '@superbet-group/web.lib.rest';

import { logger } from '@app/logger';
import { defaultHttpTimeoutMs } from '@core/constants';

import {
    StatsCompetition,
    VirtualTournament,
} from '../types';
import { getSportOfferConfig } from '../../config/sportOfferConfig';
import {
    Competition,
    StatsEvent,
} from '@core/models/base/StatsEvent';

export function createStatsService(
    host: string,
    appVariant: string,
    locale: string,
) {
    const restClient = createRestClient(
        host,
        defaultHttpTimeoutMs,
    );

    async function getCompetitionById(competitionId: number): Promise<StatsCompetition | null> {
        if (!host || !appVariant) {
            return null;
        }

        try {
            const response = await restClient.get<CompetitionDetailsDto>(`/competition/details/${appVariant}/${locale}?competition_id=${competitionId}`);
            return response ? transformCompetition(response) : null;
        } catch (error) {
            logger.error(error as Error);
            return null;
        }
    }

    async function getSoccerCompetitionById(competitionId: number): Promise<StatsCompetition | null> {
        if (!host || !appVariant) {
            return null;
        }

        try {
            const response = await restClient.get<SoccerCompetitionDetailsDto>(`/soccer/competition/details/${appVariant}/${locale}?competition_id=br:competition:${competitionId}`);
            return response ? transformSoccerCompetition(response) : null;
        } catch (error) {
            logger.error(error as Error);
            return null;
        }
    }

    async function getCompetitionByTournamentBetradarId(tournamentId: number): Promise<StatsCompetition | null> {
        try {
            const response = await restClient.get<CompetitionDetailsDto>(
                `/competition/details/tournaments/${appVariant}/${locale}?tournament_id=br:tournament:${tournamentId}`,
            );

            return response ? transformCompetition(response) : null;
        } catch (error) {
            logger.error(error as Error);
            return null;
        }
    }

    async function getCompetitionByTournamentBookieId(tournamentId: number): Promise<StatsCompetition | null> {
        try {
            const response = await restClient.get<CompetitionDetailsDto>(
                `/competition/details/tournaments/${appVariant}/${locale}?tournament_id=ax:tournament:${tournamentId}`,
            );

            return response ? transformCompetition(response) : null;
        } catch (error) {
            logger.error(error as Error);
            return null;
        }
    }

    async function getStatsTournamentResults(competitionId: number, seasonId: number): Promise<StatsEvent[]> {
        try {
            const response = await restClient.get<StatsTournamentResultsPayload>(`/competition/events/${appVariant}/${locale}?competition_id=${competitionId}&season_id=${seasonId}`);

            return response?.matches ? response.matches : [];
        } catch (e) {
            logger.error(e as Error);
            return [];
        }
    }

    async function getStatsEventResult(eventId: number): Promise<StatsEvent | null> {
        try {
            return await restClient.get(`/event/detail/${appVariant}/${locale}?id=br:match:${eventId}`);
        } catch (e) {
            logger.error(e as Error);
            return null;
        }
    }

    return {
        getCompetitionById,
        getSoccerCompetitionById,
        getCompetitionByTournamentBookieId,
        getCompetitionByTournamentBetradarId,
        getStatsTournamentResults,
        getStatsEventResult,
    };
}

type StatsTournamentResultsPayload = {
    competition: Competition
    matches: StatsEvent[]
};

const sportOfferConfig = getSportOfferConfig();

export const statsService = createStatsService(
    sportOfferConfig.statsHost,
    sportOfferConfig.appVariant,
    sportOfferConfig.locale,
);

type CompetitionDetailsDto = {
    competition: {
        id: number;
        name: string;
    };
    category: {
        name: string
        sport_id: number
    },
    category_mappings: ProviderMappingDto[];
    tournaments_mappings: ProviderMappingDto[];
    seasons: SeasonDto[]
    tournaments: TournamentDto[]
};

type ProviderMappingDto = {
    sportradaurs?: {
        value: string;
    },
    opta?: {
        value: string;
    },
    axilis: {
        value: string;
    };
    betradar: {
        value: string;
    };
};

type SoccerCompetitionDetailsDto = {
    competition : {
        id: string;
        name: string;
    };
    category: {
        id: string
        name: string
        sport_id: number
    };
    seasons: SoccerSeasonDto[]
    tournament_mappings?: SoccerProviderMappingDto[];
};

type SoccerProviderMappingDto = {
    axilis: string[];
    betradar: string[];
};

type TournamentDto = {
    name: string,
    id: 54931,
};

type SeasonDto = {
    id: number,
    name: string,
    features: number[],
};

type SoccerSeasonDto = {
    id: string,
    name: string,
    features: number[],
};

export function transformCompetitionToVirtualTournament(
    competition: StatsCompetition,
    event: {
        sportId: number;
        categoryId?: number;
    },
): VirtualTournament {
    return {
        id: `competition:vt:${competition.id}`,
        tournamentId: parseInt(`990${competition.id}`, 10),
        sportId: event.sportId,
        categoryId: competition.categoryIds?.[0] ?? event.categoryId,
        childrenIds: competition.tournamentIds,
        name: competition.name,
        competitionId: competition.id,
        currentSeasonId: competition.currentSeasonId,
        features: competition.features,
    };
}

function transformCompetition(dto: CompetitionDetailsDto): StatsCompetition {
    return {
        id: dto.competition.id,
        name: dto.competition.name,
        tournamentIds: dto.tournaments_mappings.map((tournamentMapping) => extractAxilisId(tournamentMapping.axilis.value)),
        betradarTournamentIds: dto.tournaments_mappings.map((tournamentMapping) => extractBetradarId(tournamentMapping.betradar.value)),
        categoryIds: dto.category_mappings.map((categoryMapping) => extractAxilisId(categoryMapping.axilis.value)),
        currentSeasonId: dto.seasons?.[0]?.id,
        categorySportId: dto.category.sport_id,
        features: dto.seasons?.[0]?.features ?? [],
    };
}

function transformSoccerCompetition(dto: SoccerCompetitionDetailsDto): StatsCompetition {
    return {
        id: parseInt(extractBetradarId(dto.competition.id)),
        name: dto.competition.name,
        tournamentIds: dto.tournament_mappings?.map((tournamentMapping) => extractAxilisId(tournamentMapping.axilis[0])) ?? [],
        betradarTournamentIds: dto.tournament_mappings?.map((tournamentMapping) => extractBetradarId(tournamentMapping.betradar[0])) ?? [],
        categoryIds: dto.category.id ? [parseInt(extractBetradarId(dto.category.id))] : [],
        currentSeasonId: parseInt(extractBetradarId(dto.seasons?.[0]?.id)),
        categorySportId: dto.category.sport_id,
        features: dto.seasons?.[0]?.features ?? [],
    };
}

function extractAxilisId(fullAxilisId: string): number {
    return parseInt(fullAxilisId.replace(/^ax:\w+:/, ''), 10);
}

function extractBetradarId(fullBetradarId: string): string {
    return fullBetradarId.replace(/^br:\w+:/, '');
}
